import type { Medication } from '@/types/data'
import {
  brandLabel,
  collapseSpaces,
  decodeEntities,
  isOneTimeOption,
  type MedicationOption
} from './medication-options'

/**
 * LINK orders pick their variation in three steps — Product & Dose, then Duration,
 * then Quantity — instead of one combined option. Each step only offers what the catalogue's
 * variations of the previous choices actually contain.
 *
 * Product & Dose is product + brand + strength: brand is the variant dimension that separates
 * otherwise identical variations (Salamol vs Ventolin, Adcal D3 flavours), so leaving it out
 * would make Quantity resolve to more than one variation.
 */

export type LinkVariationSelection = {
  productDose: string | null
  duration: string | null
  quantity: string | null
}

export type VariationChoice = {
  value: string
  label: string
  isInStock: boolean
}

export const EMPTY_LINK_SELECTION: LinkVariationSelection = { productDose: null, duration: null, quantity: null }

const ONE_TIME_DURATION = 'one time purchase'
const ONE_TIME_DURATION_LABEL = 'One Time Purchase'

const clean = (text: string | null | undefined) => collapseSpaces(decodeEntities(text ?? ''))

const titleOf = (medication: Medication) => clean(medication.productTitle || medication.productName)

export const productDoseKeyOf = (medication: Medication) =>
  [medication.productHash || medication.id, clean(medication.brand), clean(medication.strength)]
    .map(part => part.toLowerCase())
    .join('|')

// '' and "One Time Purchase" both mean no cadence — one duration, not two
const durationKeyOf = (medication: Medication) =>
  isOneTimeOption(medication.subscriptionFrequency)
    ? ONE_TIME_DURATION
    : clean(medication.subscriptionFrequency).toLowerCase()

const durationLabelOf = (medication: Medication) =>
  isOneTimeOption(medication.subscriptionFrequency) ? ONE_TIME_DURATION_LABEL : clean(medication.subscriptionFrequency)

const quantityKeyOf = (medication: Medication) => clean(medication.quantity).toLowerCase()

// a variation with no quantity is the product's only pack, so the variant title is the best name for it
const quantityLabelOf = (medication: Medication) =>
  clean(medication.quantity) || clean(medication.variantTitle) || titleOf(medication)

const isInStock = (medication: Medication) => medication.isInStock !== false

/** Catalogue order is kept throughout, so strengths, durations and quantities list as the product defines them. */
export const groupByProductDose = (medications: Medication[]): Map<string, Medication[]> => {
  const groups = new Map<string, Medication[]>()
  const seenHashes = new Set<string>()

  for (const medication of medications) {
    const identity = medication.variantHash || medication.wooSKU
    if (seenHashes.has(identity)) continue
    seenHashes.add(identity)

    const key = productDoseKeyOf(medication)
    const group = groups.get(key)
    if (group) group.push(medication)
    else groups.set(key, [medication])
  }

  return groups
}

/** Options for the Product & Dose combobox; `firstKey` (the ordered product & dose) lists its product first. */
export const productDoseOptions = (groups: Map<string, Medication[]>, firstKey?: string | null): MedicationOption[] => {
  const options: MedicationOption[] = []

  for (const [key, variations] of groups) {
    const [first] = variations
    const title = titleOf(first)
    const strength = clean(first.strength) || null
    const brand = brandLabel(first.brand, title)
    const dose = [brand, strength].filter(Boolean).join(' ')

    const option: MedicationOption = {
      value: key,
      label: dose ? `${title} - ${dose}` : title,
      group: title,
      strength,
      detail: brand || (strength ? '' : title),
      supply: null,
      frequency: null,
      searchText: [title, brand, strength ?? ''].join(' ').toLowerCase(),
      isInStock: variations.some(isInStock)
    }

    if (key === firstKey) options.unshift(option)
    else options.push(option)
  }

  return options
}

const choicesOf = (
  variations: Medication[],
  keyOf: (medication: Medication) => string,
  labelOf: (medication: Medication) => string
): VariationChoice[] => {
  const choices = new Map<string, VariationChoice>()

  for (const variation of variations) {
    const value = keyOf(variation)
    const existing = choices.get(value)
    if (existing) existing.isInStock ||= isInStock(variation)
    else choices.set(value, { value, label: labelOf(variation), isInStock: isInStock(variation) })
  }

  return [...choices.values()]
}

export const durationChoices = (variations: Medication[]) => choicesOf(variations, durationKeyOf, durationLabelOf)

export const quantityChoices = (variations: Medication[], duration: string) =>
  choicesOf(
    variations.filter(variation => durationKeyOf(variation) === duration),
    quantityKeyOf,
    quantityLabelOf
  )

/**
 * Drops choices the current product & dose (or duration) no longer offers, and fills a step that
 * has exactly one option — that field is locked, so the prescriber could never pick it themselves.
 * Choices that are still offered are kept, so changing only the dose keeps the duration and quantity.
 */
export const completeSelection = (
  groups: Map<string, Medication[]>,
  selection: LinkVariationSelection
): LinkVariationSelection => {
  const variations = selection.productDose ? groups.get(selection.productDose) : undefined
  if (!selection.productDose || !variations) return EMPTY_LINK_SELECTION

  const durations = durationChoices(variations)
  const duration = durations.some(choice => choice.value === selection.duration)
    ? selection.duration
    : durations.length === 1
      ? durations[0].value
      : null
  if (!duration) return { productDose: selection.productDose, duration: null, quantity: null }

  const quantities = quantityChoices(variations, duration)
  const quantity = quantities.some(choice => choice.value === selection.quantity)
    ? selection.quantity
    : quantities.length === 1
      ? quantities[0].value
      : null

  return { productDose: selection.productDose, duration, quantity }
}

/**
 * The variation a complete selection names, or null while any step is unselected. A few catalogue
 * rows are exact duplicates under different hashes; `preferredHashes` (the current pick, then the
 * ordered item) keeps a re-resolve on the same row, otherwise an in-stock row wins.
 */
export const resolveVariation = (
  groups: Map<string, Medication[]>,
  selection: LinkVariationSelection,
  preferredHashes: Array<string | null | undefined> = []
): Medication | null => {
  const { productDose, duration, quantity } = selection
  if (!productDose || !duration || quantity === null) return null

  const matches = (groups.get(productDose) ?? []).filter(
    variation => durationKeyOf(variation) === duration && quantityKeyOf(variation) === quantity
  )

  for (const hash of preferredHashes) {
    const preferred = hash ? matches.find(variation => variation.variantHash === hash) : undefined
    if (preferred) return preferred
  }

  return matches.find(isInStock) ?? matches[0] ?? null
}

/**
 * Where the three dropdowns start: the ordered variation's own choices. An order item stored at
 * product level (no variation) only names the product, so Product & Dose is preselected only when
 * that product has a single one; the remaining steps stay open for the prescriber.
 */
export const initialSelectionFor = (
  groups: Map<string, Medication[]>,
  orderedVariation: Medication | null | undefined,
  orderedItemId: string | null | undefined
): LinkVariationSelection => {
  if (orderedVariation) {
    return completeSelection(groups, {
      productDose: productDoseKeyOf(orderedVariation),
      duration: durationKeyOf(orderedVariation),
      quantity: quantityKeyOf(orderedVariation)
    })
  }

  if (!orderedItemId) return EMPTY_LINK_SELECTION

  const productDoses = [...groups.entries()]
    .filter(([, variations]) => variations[0].productHash === orderedItemId)
    .map(([key]) => key)

  return productDoses.length === 1
    ? completeSelection(groups, { ...EMPTY_LINK_SELECTION, productDose: productDoses[0] })
    : EMPTY_LINK_SELECTION
}
